'use client';

import { c as _c } from "react/compiler-runtime";
import type { OnboardingStatus } from '@/app/api/onboarding/status/types';
import type { CustomRuleInstructionSource, UpdateCustomRuleInput } from '@/lib/custom-rules/schema';
import type { CustomRule } from '@/lib/custom-rules/service';
import type { CustomRuleSource } from '@/lib/custom-rules/sources';
import type { CombinedRule } from '@/types/rules-library';
import { captureException } from '@sentry/nextjs';
import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createCustomRuleBulkRequest, createCustomRuleRequest, deleteCustomRuleRequest, deleteCustomRulesBulkRequest, fetchCustomRules, updateCustomRuleRequest, updateCustomRulesBulkRequest } from './api';
import { installationCustomRulesQueryKey } from '@/data/custom-rules/use-installation-custom-rules';
import { onboardingStatusQueryKey } from '@/data/onboarding/use-onboarding-status';
import { organizationAgentsQueryKey } from '@/data/organization-agents/query-keys';
import { BASE_MAX_ENABLED_RULES, getMaxEnabledRules } from '@/lib/custom-rule-limits';
import { buildInstructionFilesDisplaySummary } from '@/lib/custom-rules/instruction-file-display';
import { FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE, TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE } from '@/lib/custom-rules/instruction-sources';
import { rankRulesForCap } from '@/lib/custom-rules/rank-rules';
import { countsTowardAgentLimit, isOrganizationAgentRule, isRepositoryAgentRule } from '@/lib/custom-rules/rule-owner';
import { UI_CUSTOM_RULE_SOURCE, YAML_CUSTOM_RULE_SOURCE } from '@/lib/custom-rules/sources';
import { generateTempId, isTempId } from '@/utils/generate-temp-id';
const UI_MANAGED: CustomRuleSource = UI_CUSTOM_RULE_SOURCE;
const YAML_MANAGED: CustomRuleSource = YAML_CUSTOM_RULE_SOURCE;
type CustomRulesData = {
  rules: CustomRule[];
  maxEnabledRules: number;
  notRunningRuleIds: string[];
};
export const customRulesQueryKey = (installationId: number | null, selectedRepoId?: number | null) => {
  const baseKey = ['custom-rules', installationId];
  // Only add third element if repositoryId is provided
  return selectedRepoId ? [...baseKey, selectedRepoId] : baseKey;
};

/**
 * Checks if the maximum number of enabled rules has been reached. Organization agents' rows don't
 * count: they rank after the repository's own agents at review time, so they never block adding
 * or enabling one.
 *
 * @param rules - Array of custom rules to check
 * @returns True if the maximum number of enabled rules has been reached
 */
export const hasReachedEnabledRulesLimit = (rules: CustomRule[] | undefined, maxEnabledRules: number) => {
  if (!rules || !Array.isArray(rules)) {
    return false;
  }
  return rules.filter(countsTowardAgentLimit).length >= maxEnabledRules;
};

/**
 * Counts the number of enabled rules
 *
 * @param rules - Array of custom rules to count
 * @returns The number of enabled rules
 */
export const countEnabledRules = (rules: CustomRule[] | undefined) => {
  if (!rules || !Array.isArray(rules)) {
    return 0;
  }
  return rules.filter(rule => rule.enabled).length;
};

// Helper functions for handleToggleRepository
export const getRepositoryName = (rule: CombinedRule, repositoryId: number): string => {
  return rule.repositories?.find(repo => repo.id === repositoryId)?.name || 'repository';
};

/**
 * Checks if any of the specified repositories have reached the enabled rules limit
 *
 * @param rules - Array of custom rules to check
 * @param repositoryIds - Array of repository IDs to check limits for
 * @returns Object with hasLimit boolean and array of repository IDs that have reached the limit
 */
export const hasReachedEnabledRulesLimitForRepositories = (rules: CustomRule[] | undefined, repositoryIds: number[], maxEnabledRules: number) => {
  if (!rules || !Array.isArray(rules) || !repositoryIds.length) {
    return {
      hasLimit: false,
      limitedRepos: []
    };
  }
  const limitedRepos: number[] = [];
  const uniqueRepositoryIds = [...new Set(repositoryIds)];
  for (const repoId of uniqueRepositoryIds) {
    const repoRulesCount = rules.filter(rule => rule.repositoryId === repoId && countsTowardAgentLimit(rule)).length;
    if (repoRulesCount >= maxEnabledRules) {
      limitedRepos.push(repoId);
    }
  }
  return {
    hasLimit: limitedRepos.length > 0,
    limitedRepos
  };
};

/**
 * Sorts custom rules by enabled status and then by update time
 * @param rules - Array of custom rules to sort
 * @returns A new sorted array of custom rules
 */
export const sortCustomRules = (rules: CustomRule[]) => {
  return [...rules].sort((a, b) => {
    if (a.enabled && !b.enabled) {
      return -1;
    }
    if (!a.enabled && b.enabled) {
      return 1;
    }
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
};
export function useCustomRules(installationId, repositoryId, t0) {
  const $ = _c(92);
  const options = t0 === undefined ? {} : t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient) {
    t1 = targetInstallationId => {
      queryClient.invalidateQueries({
        queryKey: installationCustomRulesQueryKey(targetInstallationId)
      });
      queryClient.invalidateQueries({
        queryKey: organizationAgentsQueryKey(targetInstallationId)
      });
    };
    $[0] = queryClient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const invalidateInstallationViews = t1;
  const fallbackMaxEnabledRules = installationId !== null && installationId !== undefined ? getMaxEnabledRules(installationId) : BASE_MAX_ENABLED_RULES;
  const key = customRulesQueryKey(installationId, repositoryId);
  const {
    data: rawData,
    isLoading: isLoadingRules,
    isFetching: isFetchingRules,
    isError: isErrorRules,
    error: rulesError
  } = useQuery({
    queryKey: key,
    queryFn: () => {
      if (!installationId) {
        return Promise.resolve({
          rules: [],
          maxEnabledRules: fallbackMaxEnabledRules,
          notRunningRuleIds: []
        });
      }
      return fetchCustomRules(installationId, repositoryId);
    },
    enabled: Boolean(installationId) && (options.enabled ?? true)
  });
  const fetched = rawData ?? {
    rules: [],
    maxEnabledRules: fallbackMaxEnabledRules,
    notRunningRuleIds: []
  };
  const fetchedRules = fetched.rules;
  const maxEnabledRules = fetched.maxEnabledRules;
  const notRunningRuleIds = new Set(fetched.notRunningRuleIds);
  const rules = fetchedRules.filter(_temp);
  const editableRules = fetchedRules.filter(isRepositoryAgentRule);
  const organizationRules = fetchedRules.filter(isOrganizationAgentRule);
  const yamlRules = fetchedRules.filter(_temp2);
  const repoYamlRules = fetchedRules.filter(_temp3);
  const orgYamlRules = fetchedRules.filter(_temp4);
  const hasReachedLimit = hasReachedEnabledRulesLimit(fetchedRules, maxEnabledRules);
  const enabledRulesCount = repositoryId !== null ? rankRulesForCap({
    yamlRules,
    repositoryRules: editableRules,
    organizationRules: organizationRules.filter(rule_3 => !notRunningRuleIds.has(rule_3.id)),
    maxCustomRules: maxEnabledRules
  }).rules.length : countEnabledRules(fetchedRules.filter(rule_4 => !notRunningRuleIds.has(rule_4.id)));
  let t2;
  if ($[2] !== installationId || $[3] !== repositoryId) {
    t2 = async t3 => {
      const {
        repositoryId: repositoryIdOverride,
        title,
        description,
        instructionSource,
        instructionFilePaths,
        enabled,
        includePatterns,
        excludePatterns,
        category
      } = t3;
      const targetRepositoryId = repositoryIdOverride ?? repositoryId;
      if (!installationId || !targetRepositoryId) {
        throw new Error("Installation ID and repository ID are required");
      }
      return createCustomRuleRequest(installationId, {
        title,
        ...(instructionSource === FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE ? {
          description: description ?? "",
          instructionSource,
          instructionFilePaths: instructionFilePaths ?? []
        } : {
          description: description ?? "",
          instructionSource: TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE
        }),
        enabled,
        includePatterns: includePatterns ?? null,
        excludePatterns: excludePatterns ?? null,
        category: category ?? null,
        repositoryId: targetRepositoryId
      });
    };
    $[2] = installationId;
    $[3] = repositoryId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== fallbackMaxEnabledRules || $[6] !== installationId || $[7] !== queryClient || $[8] !== repositoryId) {
    t3 = async t4 => {
      const {
        title: title_0,
        description: description_0,
        instructionSource: t5,
        instructionFilePaths: t6,
        enabled: t7,
        category: category_0,
        repositoryId: repoIdOverride,
        includePatterns: includePatterns_0,
        excludePatterns: excludePatterns_0
      } = t4;
      const instructionSource_0 = t5 === undefined ? TEXT_CUSTOM_RULE_INSTRUCTION_SOURCE : t5;
      const instructionFilePaths_0 = t6 === undefined ? [] : t6;
      const enabled_0 = t7 === undefined ? true : t7;
      const targetRepositoryId_0 = repoIdOverride ?? repositoryId;
      if (!installationId || !targetRepositoryId_0) {
        return;
      }
      await queryClient.cancelQueries({
        queryKey: customRulesQueryKey(installationId, targetRepositoryId_0)
      });
      const previousData = queryClient.getQueryData(customRulesQueryKey(installationId, targetRepositoryId_0));
      const now = new Date().toISOString();
      const optimisticDescription = instructionSource_0 === FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE ? description_0?.trim() || buildInstructionFilesDisplaySummary(instructionFilePaths_0) : description_0 ?? "";
      const optimisticRule = {
        id: generateTempId(),
        title: title_0,
        description: optimisticDescription,
        enabled: enabled_0,
        createdAt: now,
        updatedAt: now,
        installationId,
        repositoryId: targetRepositoryId_0,
        includePatterns: includePatterns_0 ?? null,
        excludePatterns: excludePatterns_0 ?? null,
        category: category_0 ?? null,
        managedBy: UI_MANAGED,
        instructionSource: instructionSource_0,
        instructionFilePaths: instructionFilePaths_0,
        instructionSnapshot: null,
        yamlHash: null,
        lastSeenAt: null,
        yamlOrigin: null
      };
      queryClient.setQueryData(customRulesQueryKey(installationId, targetRepositoryId_0), old => ({
        rules: [optimisticRule, ...(old?.rules ?? [])],
        maxEnabledRules: old?.maxEnabledRules ?? fallbackMaxEnabledRules,
        notRunningRuleIds: old?.notRunningRuleIds ?? []
      }));
      if (installationId) {
        const previousOnboardingStatus = queryClient.getQueryData(onboardingStatusQueryKey(installationId));
        queryClient.setQueryData(onboardingStatusQueryKey(installationId), _temp5);
        return {
          previousData,
          previousOnboardingStatus,
          targetRepositoryId: targetRepositoryId_0
        };
      }
      return {
        previousData,
        targetRepositoryId: targetRepositoryId_0
      };
    };
    $[5] = fallbackMaxEnabledRules;
    $[6] = installationId;
    $[7] = queryClient;
    $[8] = repositoryId;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== installationId || $[11] !== invalidateInstallationViews || $[12] !== queryClient || $[13] !== repositoryId) {
    t4 = async (_data, variables) => {
      const targetRepositoryId_1 = variables.repositoryId ?? repositoryId;
      if (!installationId || !targetRepositoryId_1) {
        return;
      }
      const updatedRules = await customRulesQueryFn(installationId, targetRepositoryId_1);
      queryClient.setQueryData(customRulesQueryKey(installationId, targetRepositoryId_1), updatedRules);
      queryClient.invalidateQueries({
        queryKey: customRulesQueryKey(installationId)
      });
      invalidateInstallationViews(installationId);
    };
    $[10] = installationId;
    $[11] = invalidateInstallationViews;
    $[12] = queryClient;
    $[13] = repositoryId;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== installationId || $[16] !== queryClient || $[17] !== repositoryId) {
    t5 = (err, newRule, context) => {
      const targetRepositoryId_2 = context?.targetRepositoryId ?? newRule.repositoryId ?? repositoryId;
      captureException(err instanceof Error ? err : new Error(String(err)), {
        tags: {
          action: "createRule"
        },
        extra: {
          ruleTitle: newRule.title,
          installationId,
          repositoryId: targetRepositoryId_2
        }
      });
      console.error("Failed to create custom rule:", err);
      if (installationId && targetRepositoryId_2) {
        queryClient.setQueryData(customRulesQueryKey(installationId, targetRepositoryId_2), context?.previousData);
      }
      if (installationId && context?.previousOnboardingStatus) {
        queryClient.setQueryData(onboardingStatusQueryKey(installationId), context.previousOnboardingStatus);
      }
    };
    $[15] = installationId;
    $[16] = queryClient;
    $[17] = repositoryId;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = {
      mutationFn: t2,
      onMutate: t3,
      onSuccess: t4,
      onError: t5
    };
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const create = useMutation(t6);
  let t7;
  if ($[24] !== installationId) {
    t7 = async ruleData => {
      if (!installationId) {
        return;
      }
      return createCustomRuleBulkRequest(installationId, {
        ...ruleData,
        includePatterns: ruleData.includePatterns ?? null,
        excludePatterns: ruleData.excludePatterns ?? null,
        category: ruleData.category ?? null
      });
    };
    $[24] = installationId;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== installationId || $[27] !== invalidateInstallationViews || $[28] !== queryClient) {
    t8 = () => {
      queryClient.invalidateQueries({
        queryKey: customRulesQueryKey(installationId)
      });
      if (installationId) {
        invalidateInstallationViews(installationId);
      }
      queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKey(installationId)
      });
    };
    $[26] = installationId;
    $[27] = invalidateInstallationViews;
    $[28] = queryClient;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t7 || $[31] !== t8) {
    t9 = {
      mutationFn: t7,
      onError: _temp6,
      onSettled: t8
    };
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const createRuleAcrossRepositories = useMutation(t9);
  let t10;
  if ($[33] !== installationId) {
    t10 = async updatedRule => {
      if (isTempId(updatedRule.id)) {
        return;
      }
      if (!installationId) {
        throw new Error("Installation ID is required");
      }
      const {
        id: ruleId,
        includePatterns: includePatterns_1,
        excludePatterns: excludePatterns_1,
        category: category_1,
        ...rest
      } = updatedRule;
      const payload = {
        ...rest
      };
      if (includePatterns_1 !== undefined) {
        payload.includePatterns = includePatterns_1;
      }
      if (excludePatterns_1 !== undefined) {
        payload.excludePatterns = excludePatterns_1;
      }
      if (category_1 !== undefined) {
        payload.category = category_1;
      }
      return updateCustomRuleRequest(installationId, ruleId, payload);
    };
    $[33] = installationId;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== fallbackMaxEnabledRules || $[36] !== installationId || $[37] !== key || $[38] !== queryClient || $[39] !== repositoryId) {
    t11 = async updatedRule_0 => {
      await queryClient.cancelQueries({
        queryKey: customRulesQueryKey(installationId, repositoryId)
      });
      const previousData_0 = queryClient.getQueryData(customRulesQueryKey(installationId, repositoryId));
      queryClient.setQueryData(key, old_1 => ({
        rules: (old_1?.rules ?? []).map(rule_5 => {
          if (rule_5.id === updatedRule_0.id) {
            const optimisticDescription_0 = updatedRule_0.instructionSource === FILE_PATHS_CUSTOM_RULE_INSTRUCTION_SOURCE && updatedRule_0.instructionFilePaths ? updatedRule_0.description?.trim() || buildInstructionFilesDisplaySummary(updatedRule_0.instructionFilePaths) : updatedRule_0.description;
            return {
              ...rule_5,
              ...updatedRule_0,
              ...(optimisticDescription_0 !== undefined ? {
                description: optimisticDescription_0
              } : {})
            };
          }
          return rule_5;
        }),
        maxEnabledRules: old_1?.maxEnabledRules ?? fallbackMaxEnabledRules,
        notRunningRuleIds: old_1?.notRunningRuleIds ?? []
      }));
      if (installationId) {
        const previousOnboardingStatus_0 = queryClient.getQueryData(onboardingStatusQueryKey(installationId));
        const rawCurrentRules = queryClient.getQueryData(customRulesQueryKey(installationId, repositoryId));
        const currentRules = rawCurrentRules?.rules ?? [];
        const hasCustomRules = currentRules.length > 0;
        queryClient.setQueryData(onboardingStatusQueryKey(installationId), old_2 => {
          if (!old_2) {
            return old_2;
          }
          return {
            ...old_2,
            hasCreatedCustomRule: hasCustomRules,
            metadata: {
              ...old_2.metadata,
              customRuleCount: currentRules.length
            }
          };
        });
        return {
          previousData: previousData_0,
          previousOnboardingStatus: previousOnboardingStatus_0
        };
      }
      return {
        previousData: previousData_0
      };
    };
    $[35] = fallbackMaxEnabledRules;
    $[36] = installationId;
    $[37] = key;
    $[38] = queryClient;
    $[39] = repositoryId;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== installationId || $[42] !== queryClient || $[43] !== repositoryId) {
    t12 = (err_1, variables_0, context_0) => {
      captureException(err_1 instanceof Error ? err_1 : new Error(String(err_1)), {
        tags: {
          action: "updateRule"
        },
        extra: {
          ruleId: variables_0.id,
          ruleTitle: variables_0.title,
          installationId,
          repositoryId
        }
      });
      console.error("Failed to update custom rule:", err_1);
      queryClient.setQueryData(customRulesQueryKey(installationId, repositoryId), context_0?.previousData);
      if (installationId && context_0?.previousOnboardingStatus) {
        queryClient.setQueryData(onboardingStatusQueryKey(installationId), context_0.previousOnboardingStatus);
      }
    };
    $[41] = installationId;
    $[42] = queryClient;
    $[43] = repositoryId;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== installationId || $[46] !== invalidateInstallationViews || $[47] !== queryClient) {
    t13 = () => {
      queryClient.invalidateQueries({
        queryKey: customRulesQueryKey(installationId)
      });
      if (installationId) {
        invalidateInstallationViews(installationId);
      }
    };
    $[45] = installationId;
    $[46] = invalidateInstallationViews;
    $[47] = queryClient;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t10 || $[50] !== t11 || $[51] !== t12 || $[52] !== t13) {
    t14 = {
      mutationFn: t10,
      onMutate: t11,
      onError: t12,
      onSettled: t13
    };
    $[49] = t10;
    $[50] = t11;
    $[51] = t12;
    $[52] = t13;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  const update = useMutation(t14);
  let t15;
  if ($[54] !== installationId) {
    t15 = async id => {
      if (isTempId(id)) {
        return;
      }
      if (!installationId) {
        throw new Error("Installation ID is required");
      }
      return deleteCustomRuleRequest(installationId, id);
    };
    $[54] = installationId;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== fallbackMaxEnabledRules || $[57] !== installationId || $[58] !== queryClient || $[59] !== repositoryId) {
    t16 = async id_0 => {
      const mutationInstallationId = installationId;
      const mutationRepositoryId = repositoryId;
      const mutationKey = customRulesQueryKey(mutationInstallationId, mutationRepositoryId);
      await queryClient.cancelQueries({
        queryKey: mutationKey
      });
      const previousData_1 = queryClient.getQueryData(mutationKey);
      queryClient.setQueryData(mutationKey, old_3 => ({
        rules: (old_3?.rules ?? []).filter(rule_6 => rule_6.id !== id_0),
        maxEnabledRules: old_3?.maxEnabledRules ?? fallbackMaxEnabledRules,
        notRunningRuleIds: old_3?.notRunningRuleIds ?? []
      }));
      if (mutationInstallationId) {
        const previousOnboardingStatus_1 = queryClient.getQueryData(onboardingStatusQueryKey(mutationInstallationId));
        const rawCurrentRules_0 = queryClient.getQueryData(mutationKey);
        const currentRules_0 = rawCurrentRules_0?.rules ?? [];
        const remainingRulesCount = currentRules_0.length;
        queryClient.setQueryData(onboardingStatusQueryKey(mutationInstallationId), old_4 => {
          if (!old_4) {
            return old_4;
          }
          return {
            ...old_4,
            hasCreatedCustomRule: remainingRulesCount > 0,
            metadata: {
              ...old_4.metadata,
              customRuleCount: remainingRulesCount
            }
          };
        });
        return {
          previousData: previousData_1,
          previousOnboardingStatus: previousOnboardingStatus_1,
          mutationInstallationId,
          mutationRepositoryId
        };
      }
      return {
        previousData: previousData_1,
        mutationInstallationId,
        mutationRepositoryId
      };
    };
    $[56] = fallbackMaxEnabledRules;
    $[57] = installationId;
    $[58] = queryClient;
    $[59] = repositoryId;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== installationId || $[62] !== queryClient || $[63] !== repositoryId) {
    t17 = (err_2, id_1, context_1) => {
      const targetInstallationId_0 = context_1?.mutationInstallationId ?? installationId;
      const targetRepositoryId_3 = context_1?.mutationRepositoryId ?? repositoryId;
      captureException(err_2 instanceof Error ? err_2 : new Error(String(err_2)), {
        tags: {
          action: "deleteRule"
        },
        extra: {
          ruleId: id_1,
          installationId: targetInstallationId_0,
          repositoryId: targetRepositoryId_3
        }
      });
      console.error("Failed to delete custom rule:", err_2);
      queryClient.setQueryData(customRulesQueryKey(targetInstallationId_0, targetRepositoryId_3), context_1?.previousData);
      if (targetInstallationId_0 && context_1?.previousOnboardingStatus) {
        queryClient.setQueryData(onboardingStatusQueryKey(targetInstallationId_0), context_1.previousOnboardingStatus);
      }
    };
    $[61] = installationId;
    $[62] = queryClient;
    $[63] = repositoryId;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== installationId || $[66] !== invalidateInstallationViews || $[67] !== queryClient) {
    t18 = (_data_0, _error, _variables, context_2) => {
      const targetInstallationId_1 = context_2?.mutationInstallationId ?? installationId;
      if (!targetInstallationId_1) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: customRulesQueryKey(targetInstallationId_1)
      });
      invalidateInstallationViews(targetInstallationId_1);
    };
    $[65] = installationId;
    $[66] = invalidateInstallationViews;
    $[67] = queryClient;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== t15 || $[70] !== t16 || $[71] !== t17 || $[72] !== t18) {
    t19 = {
      mutationFn: t15,
      onMutate: t16,
      onError: t17,
      onSettled: t18
    };
    $[69] = t15;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
    $[73] = t19;
  } else {
    t19 = $[73];
  }
  const remove = useMutation(t19);
  let t20;
  if ($[74] !== installationId) {
    t20 = payload_0 => {
      if (!installationId) {
        throw new Error("Installation ID is required");
      }
      return updateCustomRulesBulkRequest(installationId, payload_0);
    };
    $[74] = installationId;
    $[75] = t20;
  } else {
    t20 = $[75];
  }
  let t21;
  if ($[76] !== installationId || $[77] !== invalidateInstallationViews || $[78] !== queryClient) {
    t21 = () => {
      queryClient.invalidateQueries({
        queryKey: customRulesQueryKey(installationId)
      });
      if (installationId) {
        invalidateInstallationViews(installationId);
      }
    };
    $[76] = installationId;
    $[77] = invalidateInstallationViews;
    $[78] = queryClient;
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  let t22;
  if ($[80] !== t20 || $[81] !== t21) {
    t22 = {
      mutationFn: t20,
      onError: _temp7,
      onSettled: t21
    };
    $[80] = t20;
    $[81] = t21;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  const updateBulk = useMutation(t22);
  let t23;
  if ($[83] !== installationId) {
    t23 = payload_1 => {
      if (!installationId) {
        throw new Error("Installation ID is required");
      }
      return deleteCustomRulesBulkRequest(installationId, payload_1);
    };
    $[83] = installationId;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  let t24;
  if ($[85] !== installationId || $[86] !== invalidateInstallationViews || $[87] !== queryClient) {
    t24 = () => {
      queryClient.invalidateQueries({
        queryKey: customRulesQueryKey(installationId)
      });
      if (installationId) {
        invalidateInstallationViews(installationId);
      }
      queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKey(installationId)
      });
    };
    $[85] = installationId;
    $[86] = invalidateInstallationViews;
    $[87] = queryClient;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== t23 || $[90] !== t24) {
    t25 = {
      mutationFn: t23,
      onError: _temp8,
      onSettled: t24
    };
    $[89] = t23;
    $[90] = t24;
    $[91] = t25;
  } else {
    t25 = $[91];
  }
  const deleteBulk = useMutation(t25);
  return {
    rules,
    editableRules,
    organizationRules,
    notRunningRuleIds,
    yamlRules,
    repoYamlRules,
    orgYamlRules,
    create,
    createRuleAcrossRepositories,
    update,
    updateBulk,
    remove,
    deleteBulk,
    hasReachedLimit,
    enabledRulesCount,
    isLoadingRules,
    isFetchingRules,
    isErrorRules,
    rulesError,
    maxEnabledRules
  };
}
function _temp8(err_4) {
  console.error("Failed to bulk delete custom rules:", err_4);
  throw err_4;
}
function _temp7(err_3) {
  console.error("Failed to bulk update custom rules:", err_3);
  throw err_3;
}
function _temp6(err_0) {
  console.error("Failed to create cross-repository rule:", err_0);
}
function _temp5(old_0) {
  if (!old_0) {
    return old_0;
  }
  return {
    ...old_0,
    hasCreatedCustomRule: true,
    metadata: {
      ...old_0.metadata,
      customRuleCount: (old_0.metadata?.customRuleCount || 0) + 1
    }
  };
}
function _temp4(rule_2) {
  return rule_2.yamlOrigin === "org_yaml";
}
function _temp3(rule_1) {
  return rule_1.yamlOrigin === "repo_yaml";
}
function _temp2(rule_0) {
  return rule_0.managedBy === YAML_MANAGED;
}
function _temp(rule) {
  return rule.managedBy === UI_MANAGED;
}
export async function customRulesQueryFn(installationId: number | null, repositoryId: number | null) {
  if (!installationId) {
    return {
      rules: [],
      maxEnabledRules: BASE_MAX_ENABLED_RULES,
      notRunningRuleIds: []
    };
  }
  return fetchCustomRules(installationId, repositoryId);
}
export async function prefetchCustomRules(queryClient: QueryClient, installationId: number, repositoryId: number) {
  try {
    await queryClient.prefetchQuery({
      queryKey: customRulesQueryKey(installationId, repositoryId),
      queryFn: () => fetchCustomRules(installationId, repositoryId),
      staleTime: 5 * 60 * 1000 // 5 minutes
    });
  } catch (error) {
    console.debug('Failed to prefetch custom rules:', error);
  }
}